import { useState, useMemo, useEffect } from 'react';
import { createProperty, updateProperty } from '../utils/api';
import { generatePropertyFields } from '../utils/propertyFields';
import SearchAddress from './search_address';
import { geocodeAddress, ensurePropertyCoordinates } from '../utils/propertyGeo';
import { useAuth } from '../contexts/AuthContext';

const SELECT_FIELDS = ['상권', '용도', '화장실', '주차', '담당', '담당자', '구분', '관계인', '현업종'];
const FLOOR_FIELD_KEYS = ['층수(해당/전체)', '현층/총층수'];

const FALLBACK_OPTIONS = {
  상권: ['부전동'],
  용도: ['근린생활시설', '2종근린', '근린', '점포'],
  화장실: ['내부', '공용', '복도'],
  주차: ['무', '가능'],
  담당: ['사무실'],
  담당자: ['사무실'],
  구분: ['상가 임대', '상가 매매', '사무실 임대', '사무실 매매'],
  관계인: ['건물주', '임차인', '점주'],
  현업종: ['공실', '술집', '고기집', '사무실']
};

const FIELD_SECTIONS = [
  {
    title: '기본 정보',
    description: '물건 카드 파일명과 기본 정보를 입력해주세요.',
    gridClass: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: '파일명' },
      { key: '구분' },
      { key: '상호' },
      { key: '주소', fullWidth: true }
    ]
  },
  {
    title: '규모 및 층 정보',
    description: '층 정보와 면적을 기록합니다.',
    gridClass: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: '층수(해당/전체)' },
      { key: '평수' }
    ]
  },
  {
    title: '관계 및 연락처',
    description: '현재 관계자와 연락처를 입력해주세요.',
    gridClass: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: '관계인' },
      { key: '연락처' }
    ]
  },
  {
    title: '금액 정보',
    description: '보증금, 월세 등 금액 정보를 입력합니다.',
    gridClass: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: '보증금' },
      { key: '월세' },
      { key: '관리비' },
      { key: '권리금' }
    ]
  },
  {
    title: '업종 및 시설',
    description: '현업종과 시설 정보를 기록합니다.',
    gridClass: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: '현업종' },
      { key: '용도' },
      { key: '화장실' },
      { key: '주차' }
    ]
  },
  {
    title: '일자 및 담당',
    description: '허가일과 접수일, 담당자를 입력합니다.',
    gridClass: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: '사용승인일' },
      { key: '접수일자' },
      { key: '담당자', fullWidth: true }
    ]
  },
  {
    title: '특이사항',
    description: '추가 메모를 자유롭게 남겨주세요.',
    gridClass: 'grid-cols-1',
    fields: [
      { key: '특이사항', fullWidth: true }
    ]
  }
];

function AddCard({ isOpen, onClose, onSuccess, existingProperties = [], initialData = null, isEditMode = false }) {
  const { role } = useAuth();
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [customInputs, setCustomInputs] = useState({}); // 직접 입력 모드
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false);

  // 기존 데이터에서 고유값 추출
  const uniqueValues = useMemo(() => {
    const values = {
      상권: new Set(),
      용도: new Set(),
      화장실: new Set(),
      주차: new Set(),
      담당: new Set(),
      담당자: new Set(),
      구분: new Set(),
      관계인: new Set(),
      현업종: new Set()
    };

    existingProperties.forEach(property => {
      if (property.상권) values.상권.add(property.상권);
      if (property.용도) values.용도.add(property.용도);
      if (property.화장실) values.화장실.add(property.화장실);
      if (property.담당) values.담당.add(property.담당);
      if (property.담당자) values.담당자.add(property.담당자);
      if (property.주차) values.주차.add(property.주차);
      if (property.구분 || property['구분']) values.구분.add(property.구분 || property['구분']);
      if (property.관계인) values.관계인.add(property.관계인);
      if (property.현업종) values.현업종.add(property.현업종);
    });

    const toArray = (key) => {
      const data = Array.from(values[key]).filter(Boolean);
      const merged = data.length > 0 ? data : (FALLBACK_OPTIONS[key] || []);
      return Array.from(new Set(merged)).sort();
    };

    return {
      상권: toArray('상권'),
      용도: toArray('용도'),
      화장실: toArray('화장실'),
      주차: toArray('주차'),
      담당: toArray('담당'),
      담당자: toArray('담당자'),
      구분: toArray('구분'),
      관계인: toArray('관계인'),
      현업종: toArray('현업종')
    };
  }, [existingProperties]);

  // 동적 필드 생성
  const PROPERTY_FIELDS = useMemo(
    () => generatePropertyFields(existingProperties),
    [existingProperties]
  );

  const fieldMap = useMemo(() => {
    return PROPERTY_FIELDS.reduce((acc, field) => {
      acc[field.key] = field;
      return acc;
    }, {});
  }, [PROPERTY_FIELDS]);

  const handleChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value === '' ? (PROPERTY_FIELDS.find(f => f.key === key)?.nullable ? null : undefined) : value
    }));
    setError(null);
  };

  // 층수 입력 처리
  const handleFloorChange = (fieldKey, type, value) => {
    const targetKey = fieldKey || FLOOR_FIELD_KEYS[0];
    const current = formData[targetKey] || '';
    const parts = current.split('/');
    const normalize = (input) => (input ? `${input}층` : '');

    if (type === 'current') {
      const total = parts[1] ? parts[1].replace('층', '').trim() : '';
      setFormData(prev => ({
        ...prev,
        [targetKey]: value
          ? `${normalize(value)} / ${normalize(total)}`
          : (total ? ` / ${normalize(total)}` : '')
      }));
    } else {
      const currentFloor = parts[0] ? parts[0].replace('층', '').trim() : '';
      setFormData(prev => ({
        ...prev,
        [targetKey]: currentFloor
          ? `${normalize(currentFloor)} / ${normalize(value)}`
          : (value ? ` / ${normalize(value)}` : '')
      }));
    }
  };

  // 드롭다운에서 직접 입력 모드로 전환
  const handleSelectWithCustom = (key, value) => {
    if (value === '__custom__') {
      setCustomInputs(prev => ({ ...prev, [key]: true }));
      setFormData(prev => ({ ...prev, [key]: '' }));
    } else {
      setCustomInputs(prev => ({ ...prev, [key]: false }));
      handleChange(key, value);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // 필수 필드 검증
    const requiredFields = PROPERTY_FIELDS.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !formData[f.key]);
    
    if (missingFields.length > 0) {
      setError(`필수 항목을 입력해주세요: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setIsSubmitting(true);

    try {
      // 숫자 필드 변환 및 특수 필드 처리
      const submitData = { ...formData };
      const numberFields = PROPERTY_FIELDS.filter(f => f.type === 'number').map(f => f.key);
      
      numberFields.forEach(field => {
        if (submitData[field] !== undefined && submitData[field] !== null && submitData[field] !== '') {
          submitData[field] = Number(submitData[field]);
        } else if (field === '권리금' && submitData[field] === '') {
          submitData[field] = null;
        }
      });

      // 기본값 설정
      PROPERTY_FIELDS.forEach(field => {
        if (field.default && !submitData[field.key]) {
          submitData[field.key] = field.default;
        }
      });
      
      if (!submitData.type) {
        submitData.type = '상가';
      }

      const propertyWithCoords = await ensurePropertyCoordinates(submitData);
      
      if (isEditMode && initialData?.id) {
        // 편집 모드: 업데이트
        await updateProperty(initialData.id, propertyWithCoords);
      } else {
        // 추가 모드: 생성
        await createProperty(propertyWithCoords, role); // 역할 정보 전달
      }
      
      // 폼 초기화
      setFormData({ 구분: '상가 임대' });
      setCustomInputs({});
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(isEditMode ? '매물 수정 실패:' : '매물 추가 실패:', err);
      setError(err.message || (isEditMode ? '매물 수정에 실패했습니다.' : '매물 추가에 실패했습니다.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ 구분: '상가 임대' });
    setError(null);
    setCustomInputs({});
    setIsAddressSearchOpen(false);
    onClose();
  };

  // 주소 선택 핸들러
  const handleAddressSelect = async (address) => {
    handleChange('주소', address);
    setIsAddressSearchOpen(false);
    try {
      const coords = await geocodeAddress(address);
      if (coords) {
        setFormData((prev) => ({
          ...prev,
          lat: coords.lat,
          lng: coords.lng
        }));
      }
    } catch (error) {
      console.warn('주소 지오코딩 실패:', error);
    }
  };

  const renderField = (fieldKey, options = {}) => {
    const field = fieldMap[fieldKey];
    if (!field) return null;

    const value = formData[fieldKey] ?? '';
    const wrapperClass = options.fullWidth ? 'col-span-1 md:col-span-2' : '';

    // 층수 필드
    if (FLOOR_FIELD_KEYS.includes(fieldKey)) {
      const currentValue = value || '';
      const parts = currentValue.split('/');
      const currentFloor = parts[0] ? parts[0].replace('층', '').trim() : '';
      const totalFloor = parts[1] ? parts[1].replace('층', '').trim() : '';

      return (
        <div key={fieldKey} className={`flex flex-col ${wrapperClass}`}>
          <label className="text-xs md:text-sm font-medium text-gray-700 mb-1">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <input
                type="number"
                value={currentFloor}
                onChange={(e) => handleFloorChange(fieldKey, 'current', e.target.value)}
                placeholder="현재층"
                className="w-full px-3 py-2 pr-7 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs md:text-sm"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs md:text-sm">층</span>
            </div>
            <span className="text-gray-500 text-sm">/</span>
            <div className="flex-1 relative">
              <input
                type="number"
                value={totalFloor}
                onChange={(e) => handleFloorChange(fieldKey, 'total', e.target.value)}
                placeholder="총층수"
                className="w-full px-3 py-2 pr-7 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs md:text-sm"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs md:text-sm">층</span>
            </div>
          </div>
        </div>
      );
    }

    // 주소 필드
    if (fieldKey === '주소') {
      return (
        <div key={fieldKey} className={`flex flex-col ${wrapperClass}`}>
          <label className="text-xs md:text-sm font-medium text-gray-700 mb-1">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex flex-col md:flex-row gap-2">
            <input
              type="text"
              value={value}
              readOnly
              onClick={() => setIsAddressSearchOpen(true)}
              placeholder="주소를 검색하세요"
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs md:text-sm"
            />
            <button
              type="button"
              onClick={() => setIsAddressSearchOpen(true)}
              className="px-3 py-2 bg-orange-500 text-white rounded-md text-xs md:text-sm hover:bg-orange-600 transition-colors"
            >
              주소 검색
            </button>
          </div>
        </div>
      );
    }

    // 텍스트 영역
    if (fieldKey === '특이사항' || field.type === 'textarea') {
      return (
        <div key={fieldKey} className={`flex flex-col ${wrapperClass}`}>
          <label className="text-xs md:text-sm font-medium text-gray-700 mb-1">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <textarea
            rows={4}
            value={value || ''}
            onChange={(e) => handleChange(fieldKey, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs md:text-sm resize-none"
          />
        </div>
      );
    }

    // 날짜 필드
    if (['사용승인일', '접수일자'].includes(fieldKey)) {
      return (
        <div key={fieldKey} className={`flex flex-col ${wrapperClass}`}>
          <label className="text-xs md:text-sm font-medium text-gray-700 mb-1">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="date"
            value={value || ''}
            onChange={(e) => handleChange(fieldKey, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs md:text-sm"
          />
        </div>
      );
    }

    // 선택형 필드 (상권, 용도 등)
    if (SELECT_FIELDS.includes(fieldKey)) {
      const isCustomMode = customInputs[fieldKey];
      const options = uniqueValues[fieldKey] || [];

      return (
        <div key={fieldKey} className={`flex flex-col ${wrapperClass}`}>
          <label className="text-xs md:text-sm font-medium text-gray-700 mb-1">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {isCustomMode ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={value || ''}
                onChange={(e) => handleChange(fieldKey, e.target.value)}
                placeholder="직접 입력"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs md:text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  setCustomInputs(prev => ({ ...prev, [fieldKey]: false }));
                  handleChange(fieldKey, '');
                }}
                className="px-3 py-2 text-xs md:text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                목록
              </button>
            </div>
          ) : (
            <select
              value={value || ''}
              onChange={(e) => handleSelectWithCustom(fieldKey, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs md:text-sm"
            >
              <option value="">선택하세요</option>
              {options.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
              <option value="__custom__">+ 직접 입력</option>
            </select>
          )}
        </div>
      );
    }

    // 기본 select 필드
    if (field.type === 'select') {
      return (
        <div key={fieldKey} className={`flex flex-col ${wrapperClass}`}>
          <label className="text-xs md:text-sm font-medium text-gray-700 mb-1">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <select
            value={value || field.default || ''}
            onChange={(e) => handleChange(fieldKey, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs md:text-sm"
          >
            <option value="">선택하세요</option>
            {field.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      );
    }

    // 일반 입력 필드
    return (
      <div key={fieldKey} className={`flex flex-col ${wrapperClass}`}>
        <label className="text-xs md:text-sm font-medium text-gray-700 mb-1">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
          type={field.type === 'textarea' ? 'text' : field.type || 'text'}
          value={value || ''}
          onChange={(e) => handleChange(fieldKey, e.target.value)}
          placeholder={field.placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs md:text-sm"
        />
      </div>
    );
  };

  // 모달이 열릴 때 상태 초기화 또는 편집 데이터 로드
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && initialData) {
        // 편집 모드: 기존 데이터 로드
        setFormData({ ...initialData });
      } else {
        // 추가 모드: 초기화
        setFormData({ 구분: '상가 임대' });
      }
      setError(null);
      setCustomInputs({});
      setIsAddressSearchOpen(false);
    }
  }, [isOpen, isEditMode, initialData]);

  if (!isOpen) return null;

  return (
    <>
      {/* 주소 검색 팝업 */}
      <SearchAddress
        isOpen={isAddressSearchOpen}
        onClose={() => setIsAddressSearchOpen(false)}
        onSelect={handleAddressSelect}
      />

      {/* 메인 모달 */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-xl max-h-[95vh] md:max-h-[90vh] overflow-hidden flex flex-col">
        {/* 헤더 */}
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm md:text-xl font-semibold text-gray-800">
            {isEditMode ? '매물 편집' : '새 매물 추가'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* 본문 */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-4 md:p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="space-y-6">
              {FIELD_SECTIONS.map((section) => {
                const renderedFields = section.fields
                  .map(({ key, fullWidth }) => renderField(key, { fullWidth }))
                  .filter(Boolean);

                if (renderedFields.length === 0) {
                  return null;
                }

                return (
                  <section key={section.title} className="bg-gray-50/60 border border-gray-100 rounded-xl p-4 space-y-3">
                    <div>
                      <h3 className="text-sm md:text-base font-semibold text-gray-800">{section.title}</h3>
                      {section.description && (
                        <p className="text-xs text-gray-500 mt-1">{section.description}</p>
                      )}
                    </div>
                    <div className={`grid ${section.gridClass} gap-4`}>
                      {renderedFields}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        </form>

        {/* 푸터 */}
        <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 flex items-center justify-end gap-2 md:gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-3 md:px-4 py-2 text-xs md:text-base text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            disabled={isSubmitting}
          >
            취소
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-3 md:px-4 py-2 text-xs md:text-base bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting 
              ? (isEditMode ? '저장 중...' : '추가 중...') 
              : (isEditMode ? '저장' : '추가')}
          </button>
        </div>
      </div>
      </div>
    </>
  );
}

export default AddCard;

